import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BASE } from '../constants';
import './Login.css';

const PASSWORD_REQUIREMENTS = [
  { label: 'At least 12 characters', test: (p: string) => p.length >= 12 },
  { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'One number', test: (p: string) => /\d/.test(p) },
  { label: 'One special character', test: (p: string) => /[!@#$%^&*()_+\-={};':"\\|,.<>?]/.test(p) },
];

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="lp-solo">
        <div className="lp-form">
          <div className="lp-wordmark" onClick={() => navigate('/login')}>
            <span className="lp-sq" aria-hidden />
            AGENT WORKFORCE
          </div>
          <h2 className="lp-form-title">Invalid link</h2>
          <p className="lp-form-sub">This password reset link is invalid or has expired.</p>
          <button type="button" className="lp-submit" onClick={() => navigate('/forgot-password')}>
            Request new link
          </button>
        </div>
      </div>
    );
  }

  const allMet = PASSWORD_REQUIREMENTS.every(r => r.test(password));
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!allMet) {
      setError('Password does not meet all requirements');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${BASE}/api/v1/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (res.ok) {
        navigate('/login?reset=success', { replace: true });
      } else {
        const data = await res.json().catch(() => ({}));
        const detail = data.detail;
        if (typeof detail === 'string') {
          setError(detail);
        } else if (detail?.errors) {
          setError(detail.errors.join('. '));
        } else {
          setError('Failed to reset password. The link may have expired.');
        }
      }
    } catch {
      setError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lp-solo">
      <div className="lp-form">
        <div className="lp-wordmark" onClick={() => navigate('/login')}>
          <span className="lp-sq" aria-hidden />
          AGENT WORKFORCE
        </div>

        <h2 className="lp-form-title">Reset password</h2>
        <p className="lp-form-sub">Choose a new password for your account.</p>

        {error && <div className="lp-msg err">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="lp-field">
            <label htmlFor="new-password" className="lp-label">New password</label>
            <div className="lp-pw">
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                className="lp-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter new password"
                required
                autoFocus
                autoComplete="new-password"
              />
              <button
                type="button"
                className="lp-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div className="lp-field">
            <label htmlFor="confirm-password" className="lp-label">Confirm password</label>
            <input
              id="confirm-password"
              type={showPassword ? 'text' : 'password'}
              className="lp-input"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              required
              autoComplete="new-password"
            />
          </div>

          <div className="lp-reqs">
            <div className="lp-reqs-title">Password requirements</div>
            {PASSWORD_REQUIREMENTS.map((req, i) => {
              const met = req.test(password);
              const cls = password.length === 0 ? '' : met ? 'met' : 'unmet';
              const mark = password.length === 0 ? '○' : met ? '✓' : '✗';
              return (
                <div key={i} className={`lp-req ${cls}`}>
                  <span className="lp-req-mark">{mark}</span>
                  {req.label}
                </div>
              );
            })}
            {confirmPassword.length > 0 && (
              <div className={`lp-req ${passwordsMatch ? 'met' : 'unmet'}`}>
                <span className="lp-req-mark">{passwordsMatch ? '✓' : '✗'}</span>
                Passwords match
              </div>
            )}
          </div>

          <button
            type="submit"
            className="lp-submit"
            disabled={loading || !allMet || !passwordsMatch}
          >
            {loading ? 'Resetting...' : 'Reset password'}
          </button>
        </form>
      </div>
    </div>
  );
}
